import { ApolloQueryResult, gql } from "@apollo/client";
import { BookCard } from "components/book-card";
import { CategoryNav } from "components/browse/category-nav";
import { TagsNav } from "components/browse/tags-nav";
import { ButtonGroup } from "components/button-group";
import { Button } from "components/buttons/button";
import { RenderContent } from "components/content-renderer";
import { EventCard } from "components/event-card";
import { Layout } from "components/layout";
import { PostCard } from "components/post-card";
import {
  BookCardDataFragmentDoc,
  Categories,
  EventCardDataFragmentDoc,
  GetAllCategoriesDocument,
  GetAllCategoriesQueryResult,
  GetBrowsePageContentDocument,
  GetBrowsePageContentQueryResult,
  GetBrowsePageContentQueryVariables,
  Order_By,
  PostCardDataFragmentDoc,
  useGetBrowsePageContentQuery,
} from "lib/graphql-generated";
import { addApolloState, initializeApollo } from "lib/hooks/apollo-client";
import { parseParam } from "lib/utils";
import { GetServerSideProps } from "next";
import { useRouter } from "next/router";
import querystring from "querystring";
import { logger } from "server/logger";

const GET_ALL_CATEGORIES = gql`
  query GetAllCategories {
    categories(
      order_by: [{ name: asc }]
      where: {
        _or: [
          { category_events: {} }
          { category_posts: {} }
          { category_books: {} }
        ]
      }
    ) {
      name
      slug
    }
  }
`;

const GET_BROWSE_CONTENT = gql`
  query GetBrowsePageContent(
    $today: timestamp
    $categorySlug: String_comparison_exp
    $tagFilters: books_bool_exp!
    $booksOrderBy: [books_order_by!]
    $bookLimit: Int
    $postLimit: Int
    $eventLimit: Int
    $offset: Int
  ) {
    pages_by_pk(title: "browse") {
      content
    }
    categories(where: { slug: $categorySlug }) {
      description
    }
    books(
      where: {
        _and: [
          { category_books: { category: { slug: $categorySlug } } }
          { is_unlisted: { _eq: false } }
          $tagFilters
        ]
      }
      order_by: $booksOrderBy
      limit: $bookLimit
      offset: $offset
    ) {
      ...BookCardData
    }
    books_aggregate(
      where: {
        _and: [
          { category_books: { category: { slug: $categorySlug } } }
          $tagFilters
        ]
      }
    ) {
      aggregate {
        count
      }
    }
    events(
      where: {
        _and: [
          { category_events: { category: { slug: $categorySlug } } }
          { start_time: { _gte: $today } }
        ]
      }
      order_by: { start_time: asc }

      limit: $eventLimit
      offset: $offset
    ) {
      ...EventCardData
    }
    events_aggregate(
      where: {
        _and: [
          { category_events: { category: { slug: $categorySlug } } }
          { start_time: { _gte: $today } }
        ]
      }
    ) {
      aggregate {
        count
      }
    }
    posts(
      where: { category_posts: { category: { slug: $categorySlug } } }
      order_by: { created_at: desc }
      limit: $postLimit
      offset: $offset
    ) {
      ...PostCardData
    }
    posts_aggregate(
      where: { category_posts: { category: { slug: $categorySlug } } }
    ) {
      aggregate {
        count
      }
    }
  }
  ${BookCardDataFragmentDoc}
  ${PostCardDataFragmentDoc}
  ${EventCardDataFragmentDoc}
`;

interface Props {
  categories: ({
    __typename?: "categories";
  } & Pick<Categories, "name" | "slug">)[];
  isBasePath: boolean;
  variables: GetBrowsePageContentQueryVariables;
  orderBy?: "alph" | "new";
}

type SearchType = "books" | "events" | "posts" | "all";
const viewMoreLimit = 40;
const baseBookLimit = 16;
const baseEventLimit = 4;
const basePostLimit = 6;

export const getServerSideProps: GetServerSideProps = async (ctx) => {
  const client = initializeApollo();
  const category = parseParam(ctx.params.category) || null;
  const offset = parseInt(parseParam(ctx.query.p)) || 0;
  const type: SearchType = (parseParam(ctx.query.q) as SearchType) || "all";
  const today = new Date().toISOString();
  const order = parseParam(ctx.query.o) || "alph";

  let tagIds: number[] = null;
  if (ctx.query.t && Array.isArray(ctx.query.t)) {
    tagIds = ctx.query.t.map((tag) => parseInt(tag)).filter((o) => o);
  } else {
    tagIds = [parseInt(parseParam(ctx.query.t))].filter((o) => o);
  }
  // Fetch all categories

  const {
    data: { categories },
  }: ApolloQueryResult<GetAllCategoriesQueryResult["data"]> =
    await client.query({
      query: GetAllCategoriesDocument,
    });

  const variables: GetBrowsePageContentQueryVariables = {
    categorySlug: category ? { _eq: category } : {},
    today: today,
    bookLimit:
      type === "all" ? baseBookLimit : type === "books" ? viewMoreLimit : 0,
    eventLimit:
      type === "all" ? baseEventLimit : type === "events" ? viewMoreLimit : 0,
    postLimit:
      type === "all" ? basePostLimit : type === "posts" ? viewMoreLimit : 0,
    offset:
      offset * viewMoreLimit +
      (offset !== 0 && type === "books"
        ? baseBookLimit
        : offset !== 0 && type === "posts"
        ? basePostLimit
        : offset !== 0 && type === "events"
        ? baseEventLimit
        : 0),
    tagFilters:
      tagIds.length > 0
        ? { _and: tagIds.map((id) => ({ tag_books: { tag_id: { _eq: id } } })) }
        : {},
    booksOrderBy: !category
      ? [{ created_at: Order_By.Desc }, { stock: Order_By.Desc }]
      : type === "all"
      ? [
          { category_books_aggregate: { avg: { rank_order: Order_By.Asc } } },
          { stock: Order_By.Desc },
          { created_at: Order_By.Desc },
        ]
      : [
          order === "alph"
            ? { title: Order_By.Asc }
            : { created_at: Order_By.Desc },
        ],
    // TODO:  allow ordering by date created as well for last option
  };

  const { data }: ApolloQueryResult<GetBrowsePageContentQueryResult["data"]> =
    await client.query({
      query: GetBrowsePageContentDocument,
      variables: variables,
    });

  if (!!category && !categories.some((c) => c.slug === category)) {
    logger.debug("not found");
    return addApolloState(client, {
      props: {
        isBasePath: !category,
        categories: categories,
        variables: variables,
        orderBy: order,
      } as Props,
      redirect: {
        source: "",
        destination: "/browse",
        permanent: false,
      },
    });
  }

  return addApolloState(client, {
    props: {
      isBasePath: !category,
      categories: categories,
      variables: variables,
      orderBy: order,
    } as Props,
  });
};

export const Browse = ({
  variables,
  categories,
  isBasePath,
  orderBy,
}: Props) => {
  const router = useRouter();
  const currentCategory = parseParam(router.query.category);
  const { data } = useGetBrowsePageContentQuery({ variables: variables });
  const hasNoCategory = !variables.categorySlug._eq;

  const queryType: SearchType =
    (parseParam(router.query.q) as SearchType) || null;

  const getMore = (type: SearchType) => {
    const path = router.pathname;
    const query = querystring.encode({
      p: 0,
      q: type,
      o: orderBy,
    });
    return `${path}/${currentCategory}?${query}`;
  };

  const getQueryString = (
    page: number,
    type: SearchType,
    tagIds: number[],
    order: "alph" | "new" = orderBy
  ): string => {
    const path = router.pathname;
    const query = querystring.encode({
      p: page,
      t: tagIds,
      q: type,
      o: order,
    });

    return `${path}/${currentCategory}?${query}`;
  };

  return (
    <>
      <Layout className='' pageName={isBasePath ? "Browse" : currentCategory}>
        <article className='max-w-screen-2xl mx-auto flex flex-row flex-wrap pb-16'>
          <h1 className='sr-only'>
            {currentCategory
              ? categories?.find((c) => c.slug === currentCategory).name
              : "Discover"}
          </h1>

          <CategoryNav categories={categories} isBasePath={isBasePath} />

          <section className='w-full md:w-auto md:flex-1 bg-white space-y-2 pt-12 md:pt-0'>
            <article className='w-full md:px-8 lg:px-16 flex flex-row  pt-4 text-gray-700'>
              {hasNoCategory ? (
                <RenderContent
                  content={data?.pages_by_pk.content.data[0].data}
                />
              ) : (
                <RenderContent content={data?.categories[0].description} />
              )}
            </article>
            {!hasNoCategory && (
              <TagsNav
                categories={categories}
                tagFilters={variables.tagFilters}
                hasNoCategory={hasNoCategory}
              />
            )}
            {(queryType === "posts" || queryType === null) && (
              <article className=' w-full flex flex-row flex-wrap px-4  md:px-8 lg:px-16'>
                <h2 className='header mx-auto md:mx-0 text-2xl md:text-4xl py-4 lg:py-6'>
                  Posts / News
                </h2>
                <section className='grid grid-cols-2 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-4  lg:gap-12 place-self-start w-full'>
                  {data?.posts.map((p) => (
                    <PostCard post={p} key={p.id} />
                  ))}
                </section>

                {!hasNoCategory &&
                  queryType === null &&
                  variables.offset / basePostLimit !==
                    Math.floor(
                      data?.posts_aggregate.aggregate.count / basePostLimit
                    ) && (
                    <Button
                      href={getMore("posts")}
                      buttonType='secondary'
                      buttonSize='xl'
                      className='mt-8 ml-auto mr-auto md:ml-0'>
                      View More posts in this category...
                    </Button>
                  )}
              </article>
            )}
            {(queryType === "books" || queryType === null) && (
              <article className=' w-full flex flex-row flex-wrap px-4  md:px-8 lg:px-16'>
                <header className='flex flex-row justify-between w-full'>
                  <h2 className='header  text-2xl md:text-4xl py-4 lg:py-8'>
                    {queryType === "books" ? "Books" : "New & Noteworthy"}
                  </h2>
                  {queryType === "books" && (
                    <div className=' my-auto'>
                      <ButtonGroup
                        className=''
                        buttons={[
                          {
                            onClick: () =>
                              router.push(
                                getQueryString(
                                  0,
                                  "books",
                                  variables?.tagFilters?._and
                                    ?.filter((v) => !!v.tag_books)
                                    .map((v) => v.tag_books?.tag_id._eq) || [],
                                  "alph"
                                )
                              ),
                            text: "Alphabetical",
                            selected: orderBy === "alph",
                          },
                          {
                            onClick: () =>
                              router.push(
                                getQueryString(
                                  0,
                                  "books",
                                  variables?.tagFilters?._and
                                    ?.filter((v) => !!v.tag_books)
                                    .map((v) => v.tag_books?.tag_id._eq) || [],
                                  "new"
                                )
                              ),
                            text: "Newest",
                            selected: orderBy === "new",
                          },
                        ]}
                      />
                    </div>
                  )}
                </header>
                <section className='grid grid-cols-2 gap-4 md:gap-4 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-4 lg:gap-12 place-self-start w-full'>
                  {data?.books.length > 0 ? (
                    <>
                      {data?.books.map((b) => (
                        <BookCard book={b} key={b.isbn} />
                      ))}
                    </>
                  ) : (
                    <p className='col-span-full text-gray-700 italic'>
                      There were no books matching these tags (yet...)
                    </p>
                  )}
                </section>

                {!hasNoCategory &&
                  queryType === null &&
                  variables.offset / baseBookLimit !==
                    Math.floor(
                      data?.books_aggregate.aggregate.count / baseBookLimit
                    ) && (
                    <Button
                      href={getMore("books")}
                      buttonType='secondary'
                      buttonSize='xl'
                      className='mt-8 ml-auto mr-auto md:ml-0'>
                      View More books in this category...
                    </Button>
                  )}
              </article>
            )}

            {(queryType === "events" || queryType === null) && (
              <article className=' w-full flex flex-row flex-wrap px-4  md:px-8 lg:px-16'>
                <h2 className='header mx-auto md:mx-0 text-2xl md:text-4xl py-4 lg:py-8'>
                  Related Events
                </h2>
                <section className='grid grid-cols-1 xl:grid-cols-2 gap-4 md:gap-4  lg:gap-12 place-self-start w-full'>
                  {data?.events.map((e) => (
                    <EventCard event={e} key={e.id} />
                  ))}
                </section>
                {!hasNoCategory &&
                  queryType === null &&
                  variables.offset / baseEventLimit !==
                    Math.floor(
                      data?.events_aggregate.aggregate.count / baseEventLimit
                    ) && (
                    <Button
                      href={getMore("events")}
                      buttonType='secondary'
                      buttonSize='xl'
                      className='mt-8 ml-auto mr-auto md:ml-0'>
                      View More events in this category...
                    </Button>
                  )}
              </article>
            )}
            {!hasNoCategory && queryType !== null && (
              <section className='w-full flex flex-row border-t pt-4 mt-4  px-4 md:px-8 lg:px-16'>
                {variables.offset !== 0 && (
                  <Button
                    buttonType='secondary'
                    buttonSize='xl'
                    href={getQueryString(
                      variables.offset / viewMoreLimit - 1,
                      queryType,
                      variables?.tagFilters?._and
                        ?.filter((v) => !!v.tag_books)
                        .map((v) => v.tag_books?.tag_id._eq) || []
                    )}>
                    previous
                  </Button>
                )}
                {variables.offset / viewMoreLimit !==
                  Math.floor(
                    queryType === "books"
                      ? data?.books_aggregate.aggregate.count / viewMoreLimit
                      : queryType === "events"
                      ? data?.events_aggregate.aggregate.count / viewMoreLimit
                      : data?.posts_aggregate.aggregate.count / viewMoreLimit
                  ) && (
                  <Button
                    className='ml-auto'
                    buttonType='secondary'
                    buttonSize='xl'
                    href={getQueryString(
                      variables.offset / viewMoreLimit + 1,
                      queryType,
                      variables?.tagFilters?._and
                        ?.filter((v) => !!v.tag_books)
                        .map((v) => v.tag_books?.tag_id._eq) || []
                    )}>
                    Next
                  </Button>
                )}
              </section>
            )}
          </section>
        </article>
      </Layout>
    </>
  );
};

export default Browse;
